import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { MegaphoneIcon, SquaresFourIcon } from '@phosphor-icons/react'
import { me } from '@/src/api/user'
import { getOrganizationDashboardStats } from '@/src/api/organization'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Tabs, TabsContent } from '@/src/components/ui/tabs'
import { Spinner } from '@/src/components/ui/spinner'
import { useTranslation } from '@/src/i18n/client'
import { userCanManageOrganizationSettings } from '@/src/lib/permissionHelpers'
import { OrganizationDashboardPanel } from '@/src/components/manage/organization/OrganizationDashboardPanel'
import { OrganizationAnnouncementsTab } from '@/src/components/manage/organization/OrganizationAnnouncementsTab'
import { ToolbarTabButton } from '@/src/components/layout/ToolbarTabToggle'

export const Route = createFileRoute('/_manage/manage/organization/')({
  component: OrgDashboardPage,
  validateSearch: (search: Record<string, unknown>) => {
    const tab = search.tab as string | undefined
    if (tab === 'overview' || tab === 'announcements') {
      return { tab }
    }

    return { tab: 'overview' }
  },
})

function OrgDashboardPage() {
  const { t } = useTranslation('manage')
  const navigate = Route.useNavigate()
  const { tab } = Route.useSearch()

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => me(),
  })

  const permissions: string[] = (user?.role?.permissions as string[]) ?? []
  const isAdmin = permissions.includes('*')
  const canManageOrg = userCanManageOrganizationSettings(permissions, isAdmin)
  const organization = user?.organization

  const { data: stats, isLoading: isStatsLoading } = useQuery({
    queryKey: ['organizationDashboardStats', organization?.id],
    queryFn: () => getOrganizationDashboardStats({ data: { orgId: organization!.id } }),
    enabled: !!organization?.id && canManageOrg,
  })

  const handleTabChange = (value: string) => {
    navigate({ search: { tab: value } })
  }

  if (isLoading || (canManageOrg && organization?.id && isStatsLoading)) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (!canManageOrg || !organization?.id) {
    return (
      <div>
        <AdminToolbar>
          <AdminToolbar.Left>
            <AdminToolbar.Title className="text-xl font-semibold">{t('organizations.dashboard')}</AdminToolbar.Title>
          </AdminToolbar.Left>
        </AdminToolbar>
        <div className="container py-8 text-muted-foreground">
          {!organization?.id
            ? t('organizations.noOrganization')
            : t('organizations.noPermission')}
        </div>
      </div>
    )
  }

  return (
    <div>
      <AdminToolbar>
        <AdminToolbar.Left>
          <div className="flex gap-1">
              <ToolbarTabButton active={tab === 'overview'} onClick={() => handleTabChange('overview')}>
                <SquaresFourIcon className="h-4 w-4" />
                {t('organizations.dashboardTabs.overview')}
              </ToolbarTabButton>
              <ToolbarTabButton active={tab === 'announcements'} onClick={() => handleTabChange('announcements')}>
                <MegaphoneIcon className="h-4 w-4" />
                {t('organizations.dashboardTabs.announcements')}
              </ToolbarTabButton>
          </div>
        </AdminToolbar.Left>
      </AdminToolbar>

      <div className="container py-6">
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsContent value="overview">
            <OrganizationDashboardPanel
              orgId={organization.id}
              orgName={organization.name}
              stats={stats}
            />
          </TabsContent>
          <TabsContent value="announcements">
            <OrganizationAnnouncementsTab orgId={organization.id} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
